<template>
  <div :class="`${container}`">
    <b-row
      :class="`tw-mt-[20px] align-items-start ${
        disabled ? 'tw-pointer-events-none' : ''
      } ${className}`"
    >
      <b-col v-if="label" :sm="colTitle" :class="labelClass">
        <span>
          <slot name="icon"></slot>
        </span>
        <label
          :class="`label-form tw-text-[14px] tw-font-[500] tw-leading-[100%] tw-mb-0 ${classNameLabel}`"
          :for="`inputItem.valueField`"
        >
          <div class="tw-flex tw-items-center">
            {{ label }}
            <span
              v-if="validation.required || isRequired"
              class="tw-ml-1 tw-text-[#F7553C]"
              >*</span
            >
            <slot name="rightTitle"></slot>
          </div>
          <span
            v-if="labelTooltip"
            :class="`tw-block tw-font-[400] tw-text-[13px] tw-text-[#878787] tw-leading-normal tw-mt-[4px] label-tooltip tw-w-[70%] ${labelTooltipClass}`"
            >{{ labelTooltip }}</span
          >
        </label>
      </b-col>
      <b-col :sm="colInput" :class="`${colInputClass} lg:tw-px-[13.79px]`">
        <!-- @slot Slot for the input, default as text field, can add custom input here -->
        <slot>
          <div
            v-if="!isSkeleton"
            class="tw-flex tw-gap-2 tw-items-center tw-w-full tw-relative"
          >
            <BFormInput
              :id="idFocus"
              v-model="currentValue"
              :data-cy="dataCyId"
              :readonly="readOnly"
              autocomplete="new-password"
              :type="type"
              :placeholder="placeholder"
              :class="[
                'input-base-form-input autoFocus tw-relative',
                validation.$error &&
                (!isFocus ||
                  (isFocus &&
                    ((validation?.$errors?.[0]?.$validator !== 'email' &&
                      validation?.$errors?.[0]?.$validator !== 'domain' &&
                      validation?.$errors?.[0]?.$validator !== 'phoneNumber' &&
                      validation?.$errors?.[0]?.$validator !== 'url') ||
                      validation?.isValidCharacter?.$invalid)))
                  ? 'is-invalid'
                  : '',
                disabled ? '!tw-text-[#B7B7B7]' : '',
                'no-spinner',
                inputClass,
                isLoadingAnimation ? '!tw-pr-[50px]' : '',
              ]"
              :state="validation.dirty ? validation.$error : null"
              @keydown="(evt) => emit('keydown', evt)"
              @blur="onBlur"
              @focus="onFocus"
              @input="handleInput"
            />
            <div
              v-if="isLoadingAnimation"
              class="tw-h-[38px] tw-w-[38px] tw-justify-end tw-absolute tw-right-[10px]"
            >
              <!-- eslint-disable vue/no-unused-refs -->
              <LottieAnimation
                ref="anim"
                :animation-data="ProgressAnimation"
                :auto-play="true"
                :loop="true"
              />
            </div>
            <slot name="rightInput"></slot>
          </div>

          <!-- Loading animation -->
          <BFormInput v-else class="input-base-form-skeleton" />
        </slot>

        <!-- Error message -->
        <div v-if="validation?.$errors?.[0]?.$message">
          <slot name="feedback">
            <div :class="`${validClass}`">
              <b-form-invalid-feedback
                v-if="
                  !isFocus ||
                  (isFocus &&
                    ((validation?.$errors?.[0]?.$validator !== 'email' &&
                      validation?.$errors?.[0]?.$validator !== 'domain' &&
                      validation?.$errors?.[0]?.$validator !== 'phoneNumber' &&
                      validation?.$errors?.[0]?.$validator !== 'url') ||
                      validation?.isValidCharacter?.$invalid))
                "
                :class="{ 'is-invalid': validation.$error }"
              >
                <VRuntimeTemplate
                  :template="validation?.$errors?.[0]?.$message"
                />
              </b-form-invalid-feedback>
            </div>
          </slot>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script lang="ts" setup>
import { any } from "joi";
import _ from "underscore";
import { ref, watch } from "vue";
import VRuntimeTemplate from "vue3-runtime-template";
import ProgressAnimation from "@/assets/animation/progress_light.json";
import { LottieAnimation } from "lottie-web-vue";

const props = defineProps({
  /** The model and value of the input */
  modelValue: { type: null, required: true },
  /** Vue Validation object */
  validation: { type: Object, default: () => ({}) },
  /** Flag to indicate if validation should occur on blur */
  validateOnBlur: { type: Boolean, default: false },
  /** CSS class name */
  className: { type: String, default: "" },
  dataCyId: { type: String, default: "" },
  /** CSS label class name */
  idFocus: { type: String, default: "" },
  /** CSS label class name */
  labelClass: { type: String, default: "" },
  /** CSS label class name */
  inputClass: { type: String, default: "" },
  /** Placeholder text for the input */
  placeholder: { type: String, default: "" },
  /** Label for the input */
  label: { type: String, default: "" },
  /** Tooltip text for the label */
  labelTooltip: { type: String, default: "" },
  labelTooltipClass: { type: String, default: "" },
  /** Flag to indicate if the component should be refreshed */
  isRefresh: { type: Boolean, default: false },
  /** Type of the input (e.g., 'text', 'password', etc.) */
  type: { type: String, default: "text" },
  isSkeleton: { type: Boolean, requied: false, default: false },
  colTitle: { type: Number, required: false, default: 5 },
  colInput: { type: Number, required: false, default: 7 },
  disabled: { type: Boolean, required: false, default: false },
  validClass: { type: String, required: false, default: "" },
  container: { type: String, required: false, default: "" },
  colInputClass: { type: String, required: false, default: "" },
  readOnly: { type: Boolean, required: false, default: false },
  classNameLabel: { type: String, required: false, default: "" },
  isRequired: { type: Boolean, required: false, default: false },
  isLoadingAnimation: { type: Boolean, required: false, default: false },
  isFocusSlot: { type: Boolean, required: false, default: false },
});

const isFocus = ref(false);

const emit = defineEmits([
  /** Event on value update */
  "update:modelValue",
  "blur",
  "focus",
  "keydown",
  "change",
]);
const currentValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  () => {
    props.isRefresh && (currentValue.value = props.modelValue);
    currentValue.value = props.modelValue;
  },
);

watch(
  () => props.isFocusSlot,
  () => {
    isFocus.value = props.isFocusSlot;
  },
);

const handleInput = (val: string) => {
  if (_.isEmpty(props.validation) === false) {
    if (val.trim() === "") props.validation?.$reset?.();
    else props.validation?.$touch?.();
  }
  currentValue.value = val;
  emit("update:modelValue", currentValue.value);
  emit("change", currentValue.value);
};

const onBlur = () => {
  props.validateOnBlur && props.validation?.$touch && props.validation.$touch();
  isFocus.value = false;
  emit("blur");
};

const onFocus = () => {
  isFocus.value = true;
  emit("focus");
};
</script>

<style lang="scss">
.no-spinner::-webkit-outer-spin-button,
.no-spinner::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spinner {
  -moz-appearance: textfield;
}

input.input-base-form-input {
  border-radius: 6px;
  border: 1px solid var(--ui-rule, #eff0f1);
  background: var(--ui-rule-50, rgba(239, 240, 241, 0.5));
  padding: 18px 20px;
  height: 58px;
  color: #231f20;
  font-size: 13px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  &::placeholder {
    color: #b7b7b7;
  }
}

input.input-base-form-skeleton {
  border-radius: 6px;
  border: 1px solid var(--ui-rule, #eff0f1);
  padding: 18px 20px;
  height: 58px;
  font-size: 13px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  background: #eee;
  background: linear-gradient(90deg, #ffffff 10%, #e9e9e9 25%, #ffffff 35%);
  background-size: 200% 100%;
  animation: 1.5s custom-skeleton-loading linear infinite;
}

.invalid-feedback {
  font-size: 11px !important;
  font-family: Poppins !important;
  font-style: normal !important;
  font-weight: 500 !important;
  color: #f7553c !important;
  line-height: 130% !important;
  margin-top: 10px !important;
}

.was-validated .input-base-form-input:invalid,
.input-base-form-input.is-invalid {
  border: 1px solid #f7553c;
  background: #fee5e2;

  &::after {
    content: "";
  }
}

.is-invalid {
  display: block !important;
}

.col-input-mfa {
  justify-content: end !important;
}
@media (max-width: 575px) {
  .col-input-mfa {
    justify-content: start !important;
  }
}
</style>
